import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import {
  FRAMEWORKS,
  CATEGORY_META,
  type FrameworkId,
  type DeliverableType,
  type DeliverableExportStatus,
} from '@seedgovernance/shared';
import { useWorkspace } from '../../hooks/useWorkspace';
import { apiClient } from '../../lib/api';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';

interface Deliverable {
  id: string;
  name: string;
  frameworkId: FrameworkId;
  type: DeliverableType;
  completedFields: number;
  totalFields: number;
  exportStatus: DeliverableExportStatus;
}

interface AssessmentProgress {
  answeredCount: number;
  totalCount: number;
  isComplete: boolean;
}

export function FrameworkDetail() {
  const { frameworkId } = useParams<{ frameworkId: string }>();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [assessmentProgress, setAssessmentProgress] = useState<AssessmentProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'deliverables' | 'assessment'>('deliverables');

  const framework = frameworkId ? FRAMEWORKS[frameworkId as FrameworkId] : null;

  useEffect(() => {
    if (!currentWorkspace || !frameworkId) return;
    fetchData();
  }, [currentWorkspace, frameworkId]);

  async function fetchData() {
    if (!currentWorkspace || !frameworkId) return;
    setLoading(true);
    setError(null);

    const [delResult, assessResult] = await Promise.all([
      apiClient.get<Deliverable[]>(
        `/api/tenants/${currentWorkspace.tenantId}/frameworks/${frameworkId}/deliverables`
      ),
      apiClient.get<AssessmentProgress>(
        `/api/tenants/${currentWorkspace.tenantId}/frameworks/${frameworkId}/assessment`
      ),
    ]);

    if (delResult.error) {
      setError(delResult.error.message);
    } else if (delResult.data) {
      setDeliverables(delResult.data);
    }

    if (assessResult.data) {
      setAssessmentProgress(assessResult.data);
    }

    setLoading(false);
  }

  if (!framework) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="text-center">
          <h1 className="text-2xl font-display font-bold text-white mb-2">Framework Not Found</h1>
          <p className="text-slate-400 mb-4">The framework "{frameworkId}" does not exist.</p>
          <Button variant="secondary" onClick={() => navigate('/frameworks')}>
            Back to Frameworks
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="w-8 h-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  const totalDeliverables = deliverables.length;
  const completedFields = deliverables.reduce((sum, d) => sum + d.completedFields, 0);
  const totalFields = deliverables.reduce((sum, d) => sum + d.totalFields, 0);
  const completionPct = totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
  const exportableCount = deliverables.filter((d) => d.exportStatus === 'exportable').length;

  const typeBadgeVariant = (type: DeliverableType) => {
    switch (type) {
      case 'policy': return 'brand' as const;
      case 'process': return 'info' as const;
      case 'register': return 'warning' as const;
      case 'evidence': return 'success' as const;
      default: return 'default' as const;
    }
  };

  const exportStatusIcon = (status: DeliverableExportStatus) => {
    switch (status) {
      case 'locked':
        return (
          <svg className="w-4 h-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
        );
      case 'unlockable':
        return (
          <svg className="w-4 h-4 text-yellow-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 019.9-1" />
          </svg>
        );
      case 'exportable':
        return (
          <svg className="w-4 h-4 text-growth" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20,6 9,17 4,12" />
          </svg>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link to="/frameworks" className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="15,18 9,12 15,6" />
        </svg>
        Back to Frameworks
      </Link>

      {/* Header */}
      <div className="flex items-center gap-4">
        <div
          className="w-4 h-4 rounded-full shrink-0"
          style={{ backgroundColor: framework.color }}
        />
        <div>
          <h1 className="text-2xl font-display font-bold text-white">
            {framework.name}
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">
            <Badge variant="default">{CATEGORY_META[framework.category]?.label}</Badge>
          </p>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Domains */}
      <Card>
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
          Domains
        </h2>
        <div className="flex flex-wrap gap-2">
          {framework.domains.map((domain) => (
            <Badge key={domain} variant="brand">{domain}</Badge>
          ))}
        </div>
      </Card>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-surface-mid rounded-xl p-5 border border-border">
          <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Total Deliverables</p>
          <p className="text-2xl font-display font-bold text-white">{totalDeliverables}</p>
        </div>
        <div className="bg-surface-mid rounded-xl p-5 border border-border">
          <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Completion</p>
          <p className="text-2xl font-display font-bold text-white">{completionPct}%</p>
          <div className="h-1.5 bg-surface-dark rounded-full mt-2 overflow-hidden">
            <div
              className="h-full bg-brand rounded-full transition-all"
              style={{ width: `${completionPct}%` }}
            />
          </div>
        </div>
        <div className="bg-surface-mid rounded-xl p-5 border border-border">
          <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Exportable</p>
          <p className="text-2xl font-display font-bold text-white">{exportableCount}</p>
          <p className="text-slate-500 text-xs mt-1">of {totalDeliverables} deliverables</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('deliverables')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'deliverables'
                ? 'border-brand text-white'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            Deliverables
          </button>
          <button
            onClick={() => setActiveTab('assessment')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'assessment'
                ? 'border-brand text-white'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            Assessment
          </button>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'deliverables' && (
        <div>
          {deliverables.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-400 text-sm">No deliverables yet for this framework.</p>
              <p className="text-slate-500 text-xs mt-1">
                Complete the assessment to unlock deliverables.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {deliverables.map((del) => {
                const pct = del.totalFields > 0
                  ? Math.round((del.completedFields / del.totalFields) * 100)
                  : 0;

                return (
                  <Link
                    key={del.id}
                    to={`/deliverables/${del.id}`}
                    className="flex items-center gap-4 bg-surface-mid rounded-lg border border-border p-4 hover:border-slate-600 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{del.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={typeBadgeVariant(del.type)}>{del.type}</Badge>
                        <span className="text-xs text-slate-500">
                          {del.completedFields}/{del.totalFields} fields
                        </span>
                      </div>
                    </div>
                    <div className="w-24 shrink-0">
                      <div className="h-1.5 bg-surface-dark rounded-full overflow-hidden">
                        <div
                          className="h-full bg-brand rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="text-xs text-slate-500 text-right mt-1">{pct}%</p>
                    </div>
                    <div className="shrink-0">
                      {exportStatusIcon(del.exportStatus)}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'assessment' && (
        <Card>
          {assessmentProgress ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-display font-semibold text-white">
                    Assessment Progress
                  </h3>
                  <p className="text-slate-400 text-sm mt-0.5">
                    {assessmentProgress.answeredCount} of {assessmentProgress.totalCount} questions answered
                  </p>
                </div>
                {assessmentProgress.isComplete ? (
                  <Badge variant="success">Completed</Badge>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => navigate(`/assessments/${frameworkId}`)}
                  >
                    {assessmentProgress.answeredCount > 0 ? 'Continue' : 'Start Assessment'}
                  </Button>
                )}
              </div>
              <div className="h-2 bg-surface-dark rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand rounded-full transition-all"
                  style={{
                    width: `${assessmentProgress.totalCount > 0
                      ? Math.round((assessmentProgress.answeredCount / assessmentProgress.totalCount) * 100)
                      : 0
                    }%`,
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-400 text-sm">Assessment not started.</p>
              <Button
                size="sm"
                className="mt-4"
                onClick={() => navigate(`/assessments/${frameworkId}`)}
              >
                Start Assessment
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
